import {
  Box,
  Divider,
  FormattedMessage,
  FormControlSizes,
  FormGroup,
  IconButton,
  IconName,
  isInIFrame,
  MixpanelEvent,
  MixpanelEventProperty,
  NetworkConnectionBar,
  NetworkConnectionStatusProvider,
  ThemeToggle,
  ThemeTypes,
  useAuthContext,
  useMixpanel,
  useScreenOrientation,
  useSecuritiesContext,
  useWLLayoutType,
  useWLOrgConfigContext,
  useWLThemeType,
  useWLUser,
  type CustomerUser,
  type RequiredProperties,
} from '@talos/kyoko';
import { noop } from 'lodash-es';
import { cloneElement, useCallback, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { defineMessages } from 'react-intl';
import { Route, Routes } from 'react-router-dom';
import { createGlobalStyle, useTheme } from 'styled-components';
import { MobileLabelMessages } from '../../../layouts/mobileScreens/messages';
import type { LayoutComponentScreen, SingleLayoutComponent } from '../../../layouts/types';
import { BlotterSettingsProvider } from '../../Blotters/BlotterSettingsContext';
import { Logo } from '../../Logo/Logo';
import { NoSecuritiesNotificationWrapper } from '../../RestrictedAccess';
import { Part } from '../Part';
import { useLayoutComponent } from '../useLayoutComponent';
import { usePermissionedMobileScreens } from '../usePermissionedMobileScreens';
import { MobileHeader } from './MobileHeader/MobileHeader';
import { MobileTabBar } from './MobileTabBar/MobileTabBar';
import { Menu, MenuItem, MobileLayoutWrapper, Overlay } from './styles';
import { useWebAuthn, WebAuthContextProvider } from './WebAuthn/WebAuthnContext';
import { WebAuthnToggle } from './WebAuthn/WebAuthnToggle';

const MENU_CUTOFF = 5;

export function MobileLayout() {
  const { layoutType } = useWLLayoutType();
  const orientation = useScreenOrientation();
  const isPortrait = orientation === 'portrait-primary' || orientation === 'portrait-secondary';

  const layoutComponent = useLayoutComponent(layoutType, true);

  return (
    <WebAuthContextProvider>
      <NetworkConnectionStatusProvider latencyThreshold={2000}>
        <MobileLayoutInternal layoutComponent={layoutComponent} isPortrait={isPortrait} />
      </NetworkConnectionStatusProvider>
    </WebAuthContextProvider>
  );
}

export function MobileLayoutInternal({
  layoutComponent,
  isPortrait,
}: {
  layoutComponent: SingleLayoutComponent;
  isPortrait: boolean;
}) {
  const { screens } = usePermissionedMobileScreens(layoutComponent);
  const [isOpen, setIsOpen] = useState(false);
  const user = useWLUser();

  const { tabBarItems, menuItems } = useMemo(() => {
    const tabBarItems: LayoutComponentScreen[] = [];
    const menuItems: LayoutComponentScreen[] = [];
    for (const screen of screens) {
      // we do not want to render links in the bottom tab bar
      if (screen.component && tabBarItems.length < MENU_CUTOFF) {
        tabBarItems.push(screen);
      } else {
        menuItems.push(screen);
      }
    }
    return { tabBarItems, menuItems };
  }, [screens]);

  const appRenderedScreens = useMemo(() => {
    return screens.filter<RequiredProperties<LayoutComponentScreen, 'component'>>(
      (screen): screen is RequiredProperties<LayoutComponentScreen, 'component'> => screen.component !== null
    );
  }, [screens]);

  const { securitiesList } = useSecuritiesContext();
  const noSecurities = securitiesList.length === 0;

  return (
    <>
      <MobileGlobalStyles />
      <NetworkConnectionBar />
      <MobileLayoutWrapper
        isPortrait={isPortrait}
        flex="1"
        flexDirection="column"
        gap="spacing2"
        overflow="hidden"
        position="relative"
      >
        {isPortrait && <MobileHeader onMenuClick={setIsOpen} />}
        <Part resizing={false} onMouseDown={noop} column="1" row="1" flex="1 1 0" overflow="hidden" borderRadius={0}>
          <BlotterSettingsProvider>
            <Routes>
              {appRenderedScreens.map(screen => {
                const element = noSecurities ? <NoSecuritiesNotificationWrapper /> : screen.component;
                return <Route key={screen.path} path={screen.path} element={cloneElement(element)} />;
              })}
            </Routes>
          </BlotterSettingsProvider>
        </Part>
        {isPortrait && (
          <>
            <MobileTabBar items={tabBarItems} />
            <MobileSidebarMenu isOpen={isOpen} setIsOpen={setIsOpen} menuItems={menuItems} user={user} />
          </>
        )}
      </MobileLayoutWrapper>
    </>
  );
}

const sidebarMessages = defineMessages({
  logOut: {
    id: 'Layout.MobileLayout.logOut',
    defaultMessage: 'Log out',
  },
  themeType: {
    id: 'Layout.MobileLayout.themeType',
    defaultMessage: 'Theme Type',
  },
});

function MobileSidebarMenu({
  isOpen,
  setIsOpen,
  menuItems,
  user,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  menuItems: LayoutComponentScreen[];
  user: CustomerUser;
}) {
  const theme = useTheme();
  const { themeType, setThemeType } = useWLThemeType();
  const mixpanel = useMixpanel();
  const { logout } = useAuthContext();
  const { config } = useWLOrgConfigContext();

  const handleThemeType = useCallback(
    (themeType: ThemeTypes) => {
      mixpanel.track(MixpanelEvent.ChangeThemeType, { [MixpanelEventProperty.Type]: themeType });
      setThemeType(themeType);
    },
    [mixpanel, setThemeType]
  );
  const webAuth = useWebAuthn();

  return (
    <>
      <Menu isOpen={isOpen}>
        <Logo themeType={theme.type} />
        <Box
          position="absolute"
          right="spacing16"
          top={`calc(${theme.spacing16}px + env(safe-area-inset-top))`}
          opacity={0.5}
        >
          <IconButton onClick={() => setIsOpen(false)} size={FormControlSizes.Small} icon={IconName.Close} ghost />
        </Box>
        <Divider my="spacing8" />
        {menuItems.map(item => {
          const isExternal = item.component === null && item.path.startsWith('//');
          const onClick = isExternal ? noop : () => setIsOpen(false);
          const rel = isExternal ? 'noreferrer' : undefined;
          const label = isExternal ? (
            item.label
          ) : (
            <FormattedMessage {...MobileLabelMessages[item.label as keyof typeof MobileLabelMessages]} />
          );
          const target = isExternal ? (item.target ?? '_blank') : undefined;

          return (
            <MenuItem
              key={item.path}
              to={item.path}
              data-testid={`sidebar-nav-${item.path}`}
              onClick={onClick}
              rel={rel}
              startIcon={item.icon}
              target={target}
            >
              {label}
            </MenuItem>
          );
        })}

        <Box mt="auto">
          {config.enableWebAuthn && webAuth && (
            <FormGroup
              px="spacing8"
              mb="spacing16"
              inline
              label="Require identity verification"
              justifyContent="space-between"
              alignItems="center"
            >
              <WebAuthnToggle webAuth={webAuth} />
            </FormGroup>
          )}
          <FormGroup
            px="spacing8"
            mb="spacing16"
            inline
            label={<FormattedMessage {...sidebarMessages.themeType} />}
            justifyContent="space-between"
          >
            <ThemeToggle
              themeType={themeType ?? ThemeTypes.dark}
              onChangeThemeType={handleThemeType}
              size={FormControlSizes.Default}
            />
          </FormGroup>
          {!isInIFrame() && (
            <>
              <Divider my="spacing8" />
              <MenuItem onClick={logout} startIcon={IconName.Logout}>
                <FormattedMessage {...sidebarMessages.logOut} />
              </MenuItem>
            </>
          )}
          <Divider mb="spacing8" />
          <MenuItem startIcon={IconName.UserCircle}>{user.DisplayName}</MenuItem>
        </Box>
      </Menu>
      <Overlay hidden={!isOpen} onClick={() => setIsOpen(false)} />
    </>
  );
}

const MobileGlobalStyles = createGlobalStyle`
  html {
    height: calc(100% + env(safe-area-inset-top));
  }
  html body {
    height: 100%;
    @media screen and (max-width: 768px) {
      position: fixed;
    }
  }

  input[type='color'],
  input[type='date'],
  input[type='datetime'],
  input[type='datetime-local'],
  input[type='email'],
  input[type='month'],
  input[type='number'],
  input[type='password'],
  input[type='search'],
  input[type='tel'],
  input[type='text'],
  input[type='time'],
  input[type='url'],
  input[type='week'],
  select:focus,
  textarea {
      font-size: 16px;
    }
`;
